import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { first, orderBy } from 'lodash';
import * as moment from 'moment';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, take, withLatestFrom } from 'rxjs/operators';

import { NotificationRestService } from '@app/services/notification-rest.service';
import {
  deleteNotification,
  deleteNotificationFailed,
  deleteNotificationSuccess,
  getLastNotificationList,
  getLastNotificationListFailed,
  getLastNotificationListSuccess,
  markAllAsRead,
  markAllAsReadFailed,
  markAllAsReadSuccess,
  markAnyNotificationsAsRead,
  markAsRead,
  markAsReadFailed,
  markAsReadSuccess,
  updateAnyNotifications,
} from '@main-application/notifications/store/actions/notifications.actions';
import { SnackbarErrorMessage } from '@ui-components/components/customized-snackbar/snackbar-message.enum';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';

import { selectLastAnyNotificationList, selectLastNotificationList } from '../selectors/notifications.selectors';

@Injectable()
export class NotificationsEffects {
  /**
   * get last notification list
   */
  getLastNotificationList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getLastNotificationList),
      switchMap(action =>
        this.notificationRestService.getNotifications().pipe(
          map(list => {
            const sortedList = orderBy(list || [], item => moment(item.timestamp).valueOf(), 'desc');
            return getLastNotificationListSuccess({ lastNotificationList: sortedList });
          }),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.RetrievingNotificationList);
            return of(getLastNotificationListFailed());
          })
        )
      )
    )
  );

  /**
   * mark last notification of any user any event
   */
  setAnyNotificationAsRead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(markAnyNotificationsAsRead),
      mergeMap(action =>
        this.store.select(selectLastAnyNotificationList).pipe(
          take(1),
          mergeMap(notifications => {
            const readNotifications = notifications.map(e => ({
              ...e,
              wasRead: e.wasRead || e.additionalData.turnoverIds.includes(action.turnoverId),
            }));
            return [updateAnyNotifications({ list: readNotifications })];
          })
        )
      )
    )
  );

  /**
   * set all as read
   */
  setAllAsRead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(markAllAsRead),
      mergeMap(action =>
        this.notificationRestService.markAllAsRead().pipe(
          withLatestFrom(this.store.select(selectLastNotificationList)),
          mergeMap(([, notifications]) => {
            const lastNotificationList = notifications.map(notification => ({ ...notification, wasRead: true }));
            return [markAllAsReadSuccess(), getLastNotificationListSuccess({ lastNotificationList })];
          }),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.MarkingAllNotificationsAsRead);
            return of(markAllAsReadFailed());
          })
        )
      )
    )
  );

  /**
   * mark selected as read
   */
  markAsRead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(markAsRead),
      mergeMap(action =>
        this.notificationRestService.markAsRead(action.baseNotificationBody).pipe(
          withLatestFrom(this.store.select(selectLastNotificationList)),
          mergeMap(([, notifications]) => {
            const lastNotificationList = notifications.map(item =>
              item.notificationId === first(action.baseNotificationBody.notificationIds)
                ? { ...item, wasRead: true }
                : item
            );
            return [markAsReadSuccess(), getLastNotificationListSuccess({ lastNotificationList })];
          }),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.MarkingNotificationAsRead);
            return of(markAsReadFailed());
          })
        )
      )
    )
  );
  /**
   * delete notification
   */
  deleteNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteNotification),
      mergeMap(action =>
        this.notificationRestService.delete(action.baseNotificationBody).pipe(
          withLatestFrom(this.store.select(selectLastNotificationList)),
          mergeMap(([, notifications]) => {
            const lastNotificationList = notifications.filter(
              item => item.notificationId !== first(action.baseNotificationBody.notificationIds)
            );

            return [deleteNotificationSuccess(), getLastNotificationListSuccess({ lastNotificationList })];
          }),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.DeletingNotification);
            return of(deleteNotificationFailed());
          })
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private snackbarService: SnackbarService,
    private store: Store<{}>,
    private notificationRestService: NotificationRestService
  ) {}
}
